export type Tender =
  | 'cash'
  | 'check'
  | 'other'
  | 'clover_card'
  | 'payanywhere_card';
export type SaleKind = 'sale' | 'refund' | 'chargeback';
export interface MissingCard {
  last4: string;
  authCode: string;
  tipCents: number;
  amountCents: number;
  businessDate: string;
  cloverPaymentId?: string;
  paTransactionId?: string;
}
export interface MissingSale {
  id: number;
  kind: SaleKind;
  tender: Tender;
  version: number;
  eventId: number;
  saleDate: string;
  taxAmount: string;
  tipAmount: string;
  sourceRef: string;
  grossAmount: string;
  note: string | null;
  approvalRef?: string;
  settlementKey: string;
  origin: 'ui' | 'bulk';
  customerFeeAmount: string;
  cogsAmount: string | null;
  originalMissingSaleId: number | null;
  chargebackOutcome: 'won' | 'open' | 'lost' | null;
  processorRefs: {
    batchId?: string;
    qboTxnId?: string;
    fundingId?: string;
    bankRowRef?: string;
    depositDate?: string;
    cards?: MissingCard[];
    depositAmount?: string;
  };
}
export type MissingSaleInput = Partial<MissingSale> & {
  reason: string;
  taxOverride?: boolean;
  dateOverride?: boolean;
};
export interface MissingAudit {
  id: number;
  action: string;
  reason: string;
  actorId: number;
  createdAt: string;
  toJson: MissingSale;
  fromJson: MissingSale | null;
}
export const tenderLabels: Record<Tender, string> = {
  cash: 'Cash',
  check: 'Check',
  other: 'Other',
  clover_card: 'Clover card',
  payanywhere_card: 'PayAnywhere card',
};
export const totalAmount = (row: Partial<MissingSale>) =>
  ['grossAmount', 'taxAmount', 'tipAmount', 'customerFeeAmount'].reduce(
    (sum, key) =>
      sum + Math.round(Number(row[key as keyof MissingSale] || 0) * 100),
    0
  ) / 100;

export const missingSaleDisplayTotals = (rows: MissingSale[]) => {
  const fields = [
    'grossAmount',
    'taxAmount',
    'tipAmount',
    'customerFeeAmount',
  ] as const;
  const cents = {
    taxAmount: 0,
    tipAmount: 0,
    grossAmount: 0,
    customerFeeAmount: 0,
  };
  for (const row of rows) {
    const sign =
      row.kind === 'sale'
        ? 1
        : row.kind === 'refund' || row.chargebackOutcome === 'lost'
          ? -1
          : 0;
    for (const key of fields) {
      cents[key] += sign * Math.round(Number(row[key]) * 100);
    }
  }
  return {
    taxAmount: cents.taxAmount / 100,
    tipAmount: cents.tipAmount / 100,
    grossAmount: cents.grossAmount / 100,
    customerFeeAmount: cents.customerFeeAmount / 100,
    total: fields.reduce((sum, key) => sum + cents[key], 0) / 100,
  };
};

// Owner 18 Oct 2026: a refund or chargeback can come up to six calendar
// months after its original sale, never before it. Sales keep the 30-day
// event window. The server applies the same rule.
export const MISSING_SALE_ADJUSTMENT_MONTHS = 6;
const isDay = (value?: string | null): value is string =>
  !!value &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
export const addCalendarMonths = (value: string, months: number) => {
  const date = new Date(`${value}T00:00:00Z`);
  const month = date.getUTCMonth() + months;
  const last = new Date(
    Date.UTC(date.getUTCFullYear(), month + 1, 0)
  ).getUTCDate();
  return new Date(
    Date.UTC(date.getUTCFullYear(), month, Math.min(date.getUTCDate(), last))
  )
    .toISOString()
    .slice(0, 10);
};
export const missingSaleDateError = ({
  kind,
  saleDate,
  outsideDays,
  originalSaleDate,
}: {
  kind?: SaleKind;
  saleDate?: string;
  outsideDays: number;
  originalSaleDate?: string;
}): string | undefined => {
  if (!kind || kind === 'sale') {
    return outsideDays > 30
      ? 'This date is more than 30 days outside the event and cannot be saved.'
      : undefined;
  }
  if (!isDay(saleDate) || !isDay(originalSaleDate)) {
    return undefined;
  }
  if (saleDate < originalSaleDate) {
    return `This date is before the original sale (${originalSaleDate}) and cannot be saved.`;
  }
  if (
    saleDate >
    addCalendarMonths(originalSaleDate, MISSING_SALE_ADJUSTMENT_MONTHS)
  ) {
    return `This date is more than ${MISSING_SALE_ADJUSTMENT_MONTHS} months after the original sale (${originalSaleDate}) and cannot be saved.`;
  }
  return undefined;
};

export const missingSaleErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object') {
    const value = error as {
      message?: unknown;
      data?: { error?: unknown; message?: unknown };
    };
    for (const candidate of [
      value.data?.error,
      value.data?.message,
      value.message,
    ]) {
      if (typeof candidate === 'string' && candidate) {
        return candidate;
      }
    }
  }
  return 'Unable to save. Please try again.';
};
export const missingSaleDuplicateId = (error: unknown): number | undefined => {
  const match = missingSaleErrorMessage(error).match(
    /Duplicate active missing sale #(\d+)/
  );
  return match ? Number(match[1]) : undefined;
};
